/*
 * Google Takeout (Fit / Hälsa). Riktig zip-tolkning byggs i steg 2 mot
 * live-data – här finns igenkänning och mappningshjälpen som GUI:t behöver.
 *
 * Förväntad struktur (Takeout, svensk eller engelsk locale):
 *   Takeout/Fit/Alla sessioner|All Sessions/*.json       – en fil per aktivitet
 *   Takeout/Fit/Alla data|All Data/derived_com.google.weight_*.json
 *                                  derived_com.google.height_*.json
 *                                  derived_com.google.step_count.delta_*.json
 *                                  derived_com.google.body.fat.percentage_*.json
 *   Takeout/Fit/Daglig aktivitetsstatistik|Daily activity metrics/*.csv
 *   Takeout/Fit/Aktiviteter|Activities/*.tcx               – GPS-spår
 */
import { nameSimilarity } from './importModel.js';

// Google-typer som saknar direkt googleFitType-mappning i biblioteket.
const SYNONYMS = {
  weightlifting: 'strength_other',
  strength_training: 'strength_other',
  circuit_training: 'hiit',
  diving: 'swimming',
  'swimming.pool': 'swimming',
  'stair_climbing.machine': 'stair_climbing',
  'walking.treadmill': 'walking_city',
  'walking.fitness': 'walking_brisk',
  'running.treadmill': 'treadmill',
  unknown: 'other',
  still: null,
  in_vehicle: null,
  on_foot: 'walking_city',
};

export function looksLikeTakeoutZip(file) {
  const n = (file?.name || '').toLowerCase();
  return n.endsWith('.zip') && (n.includes('takeout') || n.includes('google'));
}

// Flera bibliotekstyper delar googleFitType (t.ex. fem promenadvarianter → "walking").
// Då väljs den här "normala" varianten.
const PREFERRED = {
  walking: 'walking_city', hiking: 'hiking_forest', running: 'running', biking: 'cycling',
  swimming: 'swimming', rock_climbing: 'climbing', fencing: 'hema', boxing: 'boxing_bag',
};

// Google Fit-typ → aktivitetstyp i vårt bibliotek (googleFitType-kolumnen).
export function suggestActivityType(googleType, activityTypes) {
  const g = String(googleType || '').toLowerCase();
  if (!g) return null;
  const exact = activityTypes.filter(t => (t.googleFitType || '').toLowerCase() === g);
  if (exact.length) {
    return exact.find(t => t.key === PREFERRED[g]) || exact.slice().sort((a, b) => a.id - b.id)[0];
  }
  let hit = null;
  if (g in SYNONYMS) {
    const key = SYNONYMS[g];
    return key ? activityTypes.find(t => t.key === key) ?? null : null;
  }
  // "running.treadmill" → prova "running" osv.
  const base = g.split('.')[0];
  hit = activityTypes.find(t => (t.googleFitType || '').toLowerCase() === base);
  if (hit) return hit;
  // Sista utväg: namnlikhet mot engelska namnet.
  let best = null, bestScore = 0;
  for (const t of activityTypes) {
    const s = Math.max(nameSimilarity(g.replace(/[._]/g, ' '), t.nameEn), nameSimilarity(g.replace(/[._]/g, ' '), t.key.replace(/_/g, ' ')));
    if (s > bestScore) { bestScore = s; best = t; }
  }
  return bestScore >= 0.5 ? best : null;
}

// Gym-liknande Google-typer som typiskt är samma sak som ett loggat pass.
export const GYM_LIKE_GOOGLE_TYPES = ['strength_training', 'weightlifting', 'calisthenics', 'crossfit', 'circuit_training', 'gymnastics', 'other'];

export function buildActivityTypeMappings(activities, activityTypes) {
  const counts = new Map();
  for (const a of activities) counts.set(a.googleType, (counts.get(a.googleType) || 0) + 1);
  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1])
    .map(([googleType, count]) => {
      const suggested = suggestActivityType(googleType, activityTypes);
      return { googleType, count, action: 'map', activityTypeId: suggested?.id ?? null, autoMatched: !!suggested };
    });
}

/*
 * Läser zippen i en Web Worker (zip.js läser bara de poster som behövs) och
 * bygger bundlen. ctx.progress(label) får löpande status. Faller tillbaka på
 * huvudtråden om Worker inte kan startas.
 */
export async function parseTakeoutZip(file, ctx = {}) {
  const progress = ctx.progress || (() => {});
  const parts = await readTakeoutParts(file, progress);
  progress('Normaliserar…');
  const { buildTakeoutBundle } = await import('./takeoutParser.js');
  const bundle = buildTakeoutBundle(parts, ctx);
  const total = Object.values(bundle.datasets).reduce((n, d) => n + d.items.length, 0);
  if (total === 0) {
    throw new Error('Hittade ingen Fitness/Fit-data i zippen. Kontrollera att exporten innehåller "Fitness" (Google Fit) och att det är hela zip-filen.');
  }
  return bundle;
}

function readTakeoutParts(file, progress) {
  return new Promise((resolve, reject) => {
    let worker;
    try {
      worker = new Worker(new URL('./takeout.worker.js', import.meta.url), { type: 'module' });
    } catch (e) {
      reject(new Error(`Kunde inte starta bakgrundstråd för zip-läsning: ${e.message}`));
      return;
    }
    worker.onmessage = e => {
      const { type } = e.data;
      if (type === 'progress') progress(e.data.label);
      else if (type === 'done') { worker.terminate(); resolve(e.data.parts); }
      else if (type === 'error') { worker.terminate(); reject(new Error(e.data.message)); }
    };
    worker.onerror = err => { worker.terminate(); reject(new Error(err.message || 'Fel i zip-läsaren')); };
    worker.postMessage({ file });
  });
}
